/**
 * MarkSubmittedDialog — user confirms tender offer was filed in portal.
 *
 * Advances pipeline_stage DRAFT_OFFER → SUBMITTED → EVALUATING via Spine.
 */

import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { markTenderSubmitted } from '../../lib/api';
import { markMissionComplete, MISSIONS } from '../../lib/tutorialActions';

const t = (lang, pl, en) => (lang === 'pl' ? pl : en);

export default function MarkSubmittedDialog({ opp, lang, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await markTenderSubmitted(opp.source_id, {
        submission_reference: reference || null,
        submitted_price_pln: price ? parseFloat(price) : null,
        notes: notes || null,
      });
      toast.success(t(lang, 'Oznaczone jako złożone 📤', 'Marked as submitted 📤'));
      markMissionComplete(MISSIONS.MARK_SUBMITTED);
      setOpen(false);
      setReference('');
      setPrice('');
      setNotes('');
      if (onDone) onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || t(lang, 'Błąd zapisu', 'Save error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="default" size="sm" data-testid="mark-submitted-trigger">
            <Send className="w-3.5 h-3.5 mr-1" />
            {t(lang, 'Złożyłem ofertę', 'I submitted offer')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t(lang, 'Potwierdzenie złożenia oferty', 'Confirm offer submission')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {t(lang,
              'Potwierdź że oferta została złożona w portalu BZP / TED / e-Zamówienia. System przejdzie do statusu "ocenianie".',
              'Confirm that the offer was filed in BZP / TED / e-Procurement portal. System will transition to "evaluating".')}
          </p>

          <div>
            <Label className="text-xs">{t(lang, 'Numer potwierdzenia (z portalu)', 'Portal reference')}</Label>
            <Input
              value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder={t(lang, 'np. BZP-2026/abc123', 'e.g. BZP-2026/abc123')}
            />
          </div>

          <div>
            <Label className="text-xs">{t(lang, 'Cena oferty (PLN netto)', 'Offer price (PLN net)')}</Label>
            <Input
              type="number" step="0.01"
              value={price} onChange={(e) => setPrice(e.target.value)}
            />
          </div>

          <div>
            <Label className="text-xs">{t(lang, 'Notatki', 'Notes')}</Label>
            <Textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={t(lang,
                'Komentarz wewnętrzny — co poszło dobrze, ryzyka, kto akceptował...',
                'Internal note — what went well, risks, who approved...')}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t(lang, 'Anuluj', 'Cancel')}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="mark-submitted-save">
            {saving ? t(lang, 'Zapisuję...', 'Saving...') : t(lang, 'Potwierdź', 'Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
